import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { fmtPrice, fmtPct, pnlClass, CATEGORY_LABEL } from "@/lib/format";
import { Empty } from "@/components/kit";

export const tileColor = (pct, max) => {
  const t = Math.min(Math.abs(pct || 0) / (max || 1), 1);
  const alpha = 0.15 + t * 0.75;
  return pct >= 0 ? `hsl(var(--up) / ${alpha})` : `hsl(var(--down) / ${alpha})`;
};

const Ranking = ({ title, rows, testid, onSelect }) => (
  <div className="surface p-4" data-testid={testid}>
    <div className="eyebrow mb-3">{title}</div>
    {rows.map((a, i) => (
      <button key={a.symbol} onClick={() => onSelect(a)} className="flex w-full items-center justify-between border-b border-border py-2 text-left text-sm last:border-0 hover:text-primary transition-colors" data-testid={`${testid}-row-${a.symbol}`}>
        <span className="flex items-center gap-2 min-w-0"><span className="num w-4 text-xs text-muted-foreground">{i + 1}</span><span className="truncate">{a.name}</span></span>
        <span className="flex items-center gap-3"><span className="num text-xs text-muted-foreground">{fmtPrice(a.price, a.decimals)}</span><span className={`num font-semibold ${pnlClass(a.change_pct)}`}>{fmtPct(a.change_pct)}</span></span>
      </button>
    ))}
  </div>
);

export const MarketHeatmap = ({ onSelect, hint = "clique em um ativo para abrir o gráfico" }) => {
  const [assets, setAssets] = useState([]);
  usePoll(() => api.get("/market/assets").then((r) => setAssets(r.data.filter((a) => a.price != null))).catch(() => {}), 10000);
  const sorted = useMemo(() => [...assets].sort((a, b) => (b.change_pct || 0) - (a.change_pct || 0)), [assets]);
  const max = useMemo(() => Math.max(...assets.map((a) => Math.abs(a.change_pct || 0)), 0.5), [assets]);
  const groups = useMemo(() => Object.entries(CATEGORY_LABEL).map(([k, l]) => [l, sorted.filter((a) => a.category === k)]).filter(([, r]) => r.length), [sorted]);
  if (!assets.length) return <div className="mb-8"><Empty text="Carregando mapa do mercado…" /></div>;
  const gainers = sorted.filter((a) => a.change_pct > 0).slice(0, 5);
  const losers = sorted.filter((a) => a.change_pct < 0).reverse().slice(0, 5);
  return (
    <div className="mb-8" data-testid="market-heatmap">
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3"><span className="eyebrow">Mapa do mercado · variação do dia</span><span className="text-xs text-muted-foreground">{hint}</span></div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">
        <div className="surface p-4 space-y-4">
          {groups.map(([label, rows]) => (
            <div key={label}>
              <div className="mb-2 text-xs font-semibold text-muted-foreground">{label}</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-1.5">
                {rows.map((a) => (
                  <button key={a.symbol} onClick={() => onSelect(a)} title={`${a.name} · ${fmtPct(a.change_pct)}`} data-testid={`heatmap-tile-${a.symbol}`}
                    className="rounded-md px-2 py-2.5 text-center transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" style={{ background: tileColor(a.change_pct, max) }}>
                    <div className="num truncate text-[11px] font-semibold">{a.symbol}</div>
                    <div className="num text-xs">{fmtPct(a.change_pct)}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4 content-start">
          <Ranking title="Maiores altas" rows={gainers} testid="heatmap-gainers" onSelect={onSelect} />
          <Ranking title="Maiores quedas" rows={losers} testid="heatmap-losers" onSelect={onSelect} />
        </div>
      </div>
    </div>
  );
};
